import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ApiClient from "src/config/ApiClient";

const parseMenuResponse = (data) => {
  try {
    return data.map((menu) => ({
      menuId: menu.menu_id,
      menuName: menu.menu_name,
      schedule: typeof menu.menu_schedule === "string" ? JSON.parse(menu.menu_schedule) : (menu.menu_schedule || {}), // Safely parse or use empty object
      itemQuantity: menu.item_quantity || 0, // Ensure default value for itemQuantity
      totalPrice: parseFloat(menu.total_price || 0), // Safely parse total price
      tableNumber: menu.table_number || "", // Default to empty string if not available
      customerName: menu.customer_name || "", // Default to empty string if not available

      items:
        menu.items_info?.map((info) => {
          return {
            itemId: info.item.item_id,
            id: info.subcategory.sid,
            sid: info.subcategory.sid,
            item_type: info.subcategory?.item_type,
            item_name: info.item.item_name || "", // Default to empty string if item name is missing
            item_category: info.item.item_category,
              
            item_subcategory: info.subcategory?.item_subcategory || "", // Default to empty string if subcategory is missing
            item_image: info.subcategory.item_image, // Ensure image is correctly converted to Base64
            item_description: info.subcategory?.item_description || "", // Default to empty string if description is missing
            item_ingredients: info.subcategory?.item_ingredients || "", // Default to empty string if ingredients are missing
            item_price: info.subcategory?.item_price || 0, // Safely parse price
            price: info.subcategory?.item_price || 0,
            quantity: info.item?.quantity || 1,
            itemStatus: info.itemstatus?.item_popular
              ? JSON.parse(info.itemstatus.item_popular)
              : false, // Safe boolean parsing
            specialStatus: info.itemstatus?.item_special || false, // Default to false if not available
            offerFestival: info.itemstatus?.item_offer_festival || false, // Default to false if not available
            instantOffer: info.itemstatus?.item_instant_offer || false, // Default to false if not available
          };
        }) || [], // If no items_info, default to an empty array
    }));
  } catch (error) {
    console.error("Error in parseMenuResponse:", error);
    return []; // Return an empty array in case of an error
  }
};
// fetch menus
export const fetchMenus = createAsyncThunk(
  "menus/fetchMenus",
  async (_, { rejectWithValue }) => {
    try {
      const customerId = localStorage.getItem("customerId");

      if (!customerId) {
        return []; // No customerId, return empty list
      }

      const response = await ApiClient.get(`/menu/customer/${customerId}`);

      // If API returns no data, return empty array
      return parseMenuResponse(response.data?.data ?? []);
    } catch (error) {
      if (error.response?.status === 404) return [];
      // For other errors, reject
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// console.log("fetched menus in slicer", fetchMenus)

// Delete Menu from Server
export const deleteMenuFromServer = createAsyncThunk(
  "menus/deleteMenuFromServer",
  async (menu_id, { getState, rejectWithValue }) => {
    try {
      const customerId = localStorage.getItem("customerId");
      // const state = getState(); // Get the current state
      // const menuId = state.menus.menuId; // Use the menuId stored in the state
      // const menuId = localStorage.getItem('menu_id')
      // const menu_id = getState().menus.selectedMenuId;  // Use stored menuId
      if (!customerId || !menu_id) {
        throw new Error("Missing Customer ID or Menu ID");
      }

      await ApiClient.delete(`/menu/delete/${customerId}/${menu_id}`);
      // console.log("Delete response:", response);
      return menu_id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Delete single item from a menu
export const deleteMenuItemFromServer = createAsyncThunk(
  "menus/deleteMenuItemFromServer",
  async ({ menu_id, menuName, sid }, { rejectWithValue }) => {
    try {
      const customerId = localStorage.getItem("customerId");
      await ApiClient.delete(`/menu/delete/${customerId}/${menu_id}/${sid}`);
      // console.log("✅ Item delete response:", response);
      return { sid, menuName }; // Return item ID and menuName for reducer to remove
    } catch (error) {
      console.error("❌ Item delete error:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateMenuScheduleOnServer = createAsyncThunk(
  "menus/updateMenuScheduleOnServer",
  async ({ menuName, date, time }, { getState, rejectWithValue }) => {
    try {
      const customerId = localStorage.getItem("customerId");
      const menu_id = getState().menus.selectedMenus.find((menu) => menu.menuName === menuName)?.menu_id;

      await ApiClient.patch(
        `/menu/patch/${customerId}/${menu_id}`,
        {
          menuSchedule: {
            date,
            time,
          },
        }
      );

      return { menuName, date, time };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
const initialState = {
  menus: {},
  currentMenuId: null,
  selectedMenus: [],
  selectedMenuId: null,
  currentMenu: null,
  isDateTimeDialogOpen: false,
  currentMenuIndex: null,
  openMenuDialog: false,
  confirmDeleteDialog: false,
  openItemDelete: false,
  deleteMenuName: "",
  menuName: "",
  anchorElId: null,
  selectedCategory: "biryani",
  foodItemToAdd: null,
  popoverID: null,
  deletePopoverId: null,
  newDate: "",
  newTime: "",
  location: { lat: null, lon: null },
  menuDates: [],
  menuTimes: [],
  joinData: [],
  catalogStatus: "idle",
  loading: false,
  error: null,
  selectedItems: [],
  // isDateTimeDialogOpen: false,
  selectedDate: "",
  selectedTime: "",
};

const menuSlice = createSlice({
  name: "menus",
  initialState,
  reducers: {
    addMenu: (state, action) => {
      const { menuName } = action.payload;
      if (!state.selectedMenus.some((menu) => menu.menuName === menuName)) {
        const newMenu = { menuName, items: [], totalPrice: 0 };
        state.selectedMenus.push(newMenu);
      }
    },
    addItemToMenu: (state, action) => {
      const { menuName, items } = action.payload;
      const menu = state.selectedMenus.find(
        (menu) => menu.menuName === menuName
      );

      if (!menu) {
        console.error("Menu not found for menuName:", menuName);
        return;
      }

      if (!Array.isArray(items)) {
        console.warn("`items` is not a valid array in addItemToMenu", items);
        return;
      }

      items.forEach((newItem) => {
        const newItemId = newItem.sid ?? newItem.id ?? newItem.item_id;
        // Check if the item already exists
        const existingItem = menu.items.find(
          (item) => (item.sid ?? item.id ?? item.item_id) === newItemId
        );

        if (existingItem) {
          // Update quantity and totalPrice if it already exists
          existingItem.quantity += newItem.quantity || 1;
          existingItem.totalPrice = existingItem.quantity * existingItem.price;
        } else {
          // Add new item with default quantity 1 if not provided
          menu.items.push({
            ...newItem,
            quantity: newItem.quantity || 1,
            totalPrice: (newItem.quantity || 1) * newItem.price,
          });
        }
      });

      // Update totalPrice for the menu
      menu.totalPrice = menu.items.reduce(
        (total, item) => total + item.totalPrice,
        0
      );

      // Update currentMenu if it matches
      if (state.currentMenu?.menuName === menuName) {
        state.currentMenu = { ...menu };
      }

      // Optionally, set menu_id
      state.menu_id = menu.menu_id;
    },

    updateMenu: (state, action) => {
      const { menuName, items } = action.payload;

      if (!menuName || !Array.isArray(items)) {
        console.error("Invalid payload for updateMenu:", action.payload);
        return;
      }

      let menu = state.selectedMenus.find((menu) => menu.menuName === menuName);

      if (!menu) {
        // If menu doesn't exist, create it with the given items
        const newMenu = {
          menuName,
          items: [...items],
        };
        state.selectedMenus.push(newMenu);
        if (state.currentMenu?.menuName === menuName) {
          state.currentMenu = { ...newMenu };
        }
        return;
      }

      // Update existing items or add new items
      items.forEach((incomingItem) => {
        const existingItem = menu.items.find((i) => i.sid === incomingItem.sid);
        if (existingItem) {
          existingItem.quantity += incomingItem.quantity;
          existingItem.totalPrice = existingItem.quantity * existingItem.price;
        } else {
          menu.items.push({ ...incomingItem });
        }
      });

      // Calculate totalPrice for the updated menu
      menu.totalPrice = menu.items.reduce(
        (sum, item) => sum + item.quantity * item.price,
        0
      );

      if (state.currentMenu?.menuName === menuName) {
        state.currentMenu = { ...menu };
      }
    },
    openDateTimeDialog: (state, action) => {
      state.isDateTimeDialogOpen = true;
    },
    closeDateTimeDialog: (state) => {
      state.isDateTimeDialogOpen = false;
    },
    setMenuDate: (state, action) => {
      const { menuName, date } = action.payload;
      const menu = state.selectedMenus.find((m) => m.menuName === menuName);
      if (menu) {
        menu.date = date;
      }
      state.selectedDate = date;
    },
    setMenuTime: (state, action) => {
      const { menuName, time } = action.payload;
      const menu = state.selectedMenus.find((m) => m.menuName === menuName);
      if (menu) {
        menu.time = time;
      }
      state.selectedTime = time;
    },
    updateMenuDateTime: (state, action) => {
      const { menuName, date, time } = action.payload;

      const menu = state.selectedMenus.find((m) => m.menuName === menuName);
      if (menu) {
        menu.schedule = { date, time };
        state.selectedDate = date;
        state.selectedTime = time;
      }
    },

    // Delete menu
    deleteMenu: (state, action) => {
      const menuName = action.payload;
      state.selectedMenus = state.selectedMenus.filter(
        (menu) => menu.menuName !== menuName
      );
      if (state.currentMenu?.menuName === menuName) {
        state.currentMenu = null;
      }
      // console.log("delete menu name", deleteMenu);
    },
    incrementMenuItemQuantity: (state, action) => {
      const { menuName, itemId } = action.payload;
      const menu = state.selectedMenus.find((m) => m.menuName === menuName);

      if (!menu) {
        console.error(`Menu "${menuName}" not found.`);
        return;
      }
      console.log("menu in slice increment", menu);

      const item = menu.items?.find((i) => (i.id ?? i.sid) === itemId);
      if (item) {
        item.quantity += 1;
        item.totalPrice = item.quantity * item.price;
        menu.totalPrice = menu.items.reduce(
          (total, menuItem) => total + (menuItem.totalPrice || 0),
          0
        );
      } else {
        console.error(
          `Item with sid "${itemId}" not found in menu "${menuName}".`
        );
      }
    },
    decrementMenuItemQuantity: (state, action) => {
      const { menuName, itemId } = action.payload;
      const menu = state.selectedMenus.find((m) => m.menuName === menuName);

      if (!menu) {
        console.error(`Menu "${menuName}" not found.`);
        return;
      }
      // console.log("menu in slice increment", menu);

      const item = menu.items?.find((i) => (i.id ?? i.sid) === itemId);
      if (item) {
        item.quantity = Math.max(1, item.quantity - 1);
        item.totalPrice = item.quantity * item.price;
        menu.totalPrice = menu.items.reduce(
          (total, menuItem) => total + (menuItem.totalPrice || 0),
          0
        );
      } else {
        console.error(
          `Item with sid "${itemId}" not found in menu "${menuName}".`
        );
      }
    },
    // Remove item from menu
    removeItemFromMenu: (state, action) => {
      const { menuName, itemId } = action.payload;

      const menu = state.selectedMenus.find(
        (menu) => menu.menuName === menuName
      );
      if (!menu) return;

      // Remove the item by matching ID
      menu.items = menu.items.filter((item) => item.id !== itemId);

      // Recalculate total price
      menu.totalPrice = menu.items.reduce(
        (total, item) => total + (item.totalPrice || 0),
        0
      );

      // Update currentMenu if it's the same menu
      if (state.currentMenu?.menuName === menuName) {
        state.currentMenu = { ...menu };
      }
    },

    setAnchorElId: (state, action) => {
      state.anchorElId = action.payload;
    },
    openPopover: (state, action) => {
      const { popoverID, anchorElId, foodItem } = action.payload || {};
      // console.log("payload in open popover", action.payload);
      state.openPopover = !!popoverID;
      state.popoverID = popoverID || null; //food item id (sid)
      state.anchorElId = anchorElId || null; //event id
      state.foodItemToAdd = foodItem || null; //fooditem to be dded
      // console.log("Popover opened with ID:", state.popoverID);
    },
    closePopover: (state) => {
      state.openPopover = false;
      state.anchorElId = null;
      state.popoverID = null;
      state.menuName = null;
    },
    openMenuNameDialog: (state, action) => {
      const { foodItem, anchorElId, popoverID } = action.payload || {};
      //  console.log('openMenuNameDialog payload:', action.payload);
      // Log payload for debugging
      state.openMenuDialog = true;
      state.popoverID = popoverID; //fooditem id
      state.anchorElId = anchorElId;
      state.menuName = action.payload.menuName || "";
      state.foodItemToAdd = foodItem;
    },
    closeMenuNameDialog: (state) => {
      state.openMenuDialog = false;
      state.anchorElId = null;
      state.popoverID = null;
      state.foodItemToAdd = null;
      state.menuName = null;
      // state.currentMenu = null;
      // console.log("setting current menu to null in closing menu dialog..");
    },
    openConfirmDeleteDialog: (state, action) => {
      const { menuName, menu_id } = action.payload;
      // console.log("opening delete dialog", menuName, menu_id);
      state.confirmDeleteDialog = true;
      state.menuName = menuName;
      state.menu_id = menu_id;
      state.deleteMenuName = action.payload;
    },
    closeConfirmDeleteDialog: (state) => {
      state.confirmDeleteDialog = false;
      state.deleteMenuName = null;
    },
    openItemDeleteDialog: (state, action) => {
      const { deletePopoverId, anchorElId, menuName, fooditem } =
        action.payload || {};
      // console.log("payload in item delete dialog", action.payload);
      state.openItemDelete = !!deletePopoverId;
      state.deletePopoverId = deletePopoverId;
      state.anchorElId = anchorElId;
      state.menuName = menuName;
      state.fooditem = fooditem;
      // console.log("popvoer state id in open item delete", state.deletePopoverId)
    },
    closeItemDeleteDialog: (state) => {
      state.openItemDelete = false;
      state.deletePopoverId = null;
      state.anchorElId = null;
      state.menuName = null;
      state.fooditem = null;
    },
    setMenuName: (state, action) => {
      state.menuName = action.payload;
    },
    setDeleteMenuName: (state, action) => {
      state.deleteMenuName = action.payload;
    },
    setSelectedMenuId: (state, action) => {
      state.selectedMenuId = action.payload; // Update the selected menu ID
      // console.log(
      //   "action payload menu id selection in deletion",
      //   action.payload
      // );
    },
    setCurrentMenu: (state, action) => {
      state.currentMenu = action.payload;
      // console.log("action payload in setting current menu", action.payload);
    },
    setCurrentMenuIndex: (state, action) => {
      state.currentMenuIndex = action.payload;
    },
    createMenuFromItems: (state, action) => {
      const { menuName, selectedItems } = action.payload;
      const menu = state.selectedMenus.find(
        (menu) => menu.menuName === menuName
      );
      if (menu) {
        menu.items = [...menu.items, ...selectedItems];
      }
    },
    setFoodItemToAdd: (state, action) => {
      state.foodItemToAdd = action.payload;
    },
    setSelectedCategory: (state, action) => {
      state.selectedCategory = action.payload;
    },
    setSelectedMenus: (state, action) => {
      state.selectedMenus = action.payload;
    },
    setJoinData: (state, action) => {
      state.joinData = action.payload;
    },
    fetchJoinDataRequest: (state) => {
      state.catalogStatus = "loading";
      state.loading = true;
      state.error = null;
    },
    fetchJoinDataSuccess: (state, action) => {
      state.catalogStatus = "success";
      state.joinData = action.payload;
      state.loading = false;
    },
    fetchJoinDataFailure: (state, action) => {
      state.catalogStatus = "error";
      state.error = action.payload;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMenus.pending, (state) => {
        state.loading = true;
        // console.log("fetch menus pending")
        state.error = null;
      })
      .addCase(fetchMenus.fulfilled, (state, action) => {
        state.menus = action.payload;
        state.selectedMenus = action.payload.map((menu) => ({
          menu_id: menu.menuId,
          menuName: menu.menuName,
          items: menu.items || [],
          totalPrice: menu.totalPrice || 0,
          date: menu.schedule?.date || null,
          time: menu.schedule?.time || null,
        }));
        state.currentMenu = state.selectedMenus.find(
          (menu) => String(menu.menu_id) === String(state.selectedMenuId)
        ) || state.selectedMenus[0] || null;
        state.selectedMenuId = state.currentMenu?.menu_id || null;
        // console.log("fetch menus fulfilled", state.menus, state.selectedMenus)
        state.loading = false;
      })
      .addCase(fetchMenus.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
        // console.log('fetch menus rejected')
      })
      //deleting menu extra menucers
      .addCase(deleteMenuFromServer.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteMenuFromServer.fulfilled, (state, action) => {
        state.loading = false;
        // Remove the deleted menu from the selectedMenus array
        state.selectedMenus = state.selectedMenus.filter(
          (menu) => menu.menuName !== action.payload
        );
        state.selectedMenus = state.selectedMenus.filter(
          (menu) => menu.menu_id !== action.payload
        );
        state.selectedMenuId = null;
        if (state.currentMenu?.menuName === action.payload) {
          state.currentMenu = null;
        }
      })
      .addCase(deleteMenuFromServer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      //deleting item from the menu
      // Item deletion
      .addCase(deleteMenuItemFromServer.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteMenuItemFromServer.fulfilled, (state, action) => {
        state.loading = false;

        const { sid, menuName } = action.payload;

        const menu = state.selectedMenus.find(
          (menu) => menu.menuName === menuName
        );
        if (menu) {
          menu.items = menu.items.filter((item) => String(item.sid ?? item.id) !== String(sid));
          if (state.currentMenu?.menuName === menuName) state.currentMenu = { ...menu };
          menu.totalPrice = menu.items.reduce(
            (total, item) => total + (item.totalPrice || 0),
            0
          );
        }

        // Close the delete popover
        state.openItemDelete = false;
        state.deletePopoverId = null;
        state.anchorElId = null;
        state.fooditem = null;
        state.menuName = null;
      })
      .addCase(deleteMenuItemFromServer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to delete item";
      })
      .addCase(updateMenuScheduleOnServer.fulfilled, (state, action) => {
        const { menuName, date, time } = action.payload;
        const menu = state.selectedMenus.find((m) => m.menuName === menuName);
        if (menu) {
          menu.date = date;
          menu.time = time;
          if (state.currentMenu?.menuName === menuName) state.currentMenu = { ...menu };
        }
      });
  },
});

export const {
  addMenu,
  updateMenu,
  setMenuDate,
  setMenuTime,
  openDateTimeDialog,
  closeDateTimeDialog,
  updateMenuDateTime,
  deleteMenu,
  addItemToMenu,
  removeItemFromMenu,
  incrementMenuItemQuantity,
  decrementMenuItemQuantity,
  openPopover,
  closePopover,
  openMenuNameDialog,
  closeMenuNameDialog,
  openConfirmDeleteDialog,
  closeConfirmDeleteDialog,
  openItemDeleteDialog,
  closeItemDeleteDialog,
  setMenuName,
  selectedMenus,
  setDeleteMenuName,
  setCurrentMenu,
  setSelectedMenuId,
  setCurrentMenuIndex,
  createMenuFromItems,
  setAnchorElId,
  setSelectedCategory,
  setFoodItemToAdd,
  setJoinData,
  fetchJoinDataRequest,
  fetchJoinDataSuccess,
  fetchJoinDataFailure,
} = menuSlice.actions;

// export const fetchMenus = menuSlice.actions.fetchMenus; // Include only if fetchMenus is explicitly defined.

export const joinDataSelector = (state) => state.menus.joinData;
export const fetchJoinData = () => async (dispatch) => {
  dispatch(fetchJoinDataRequest());
  try {
    const response = await ApiClient.get(`/joinData`);
    const data = response.data.data; // Adjust if needed
    // console.log("API Response:", data);
    if (Array.isArray(data)) {
      // Preserve the nested rows expected by existing menu components.
      dispatch(fetchJoinDataSuccess([Array.isArray(data[0]) ? data[0] : data]));
    } else {
      dispatch(fetchJoinDataFailure("Data is not an array"));
    }
  } catch (error) {
    dispatch(fetchJoinDataFailure(error.message));
  }
};

export default menuSlice.reducer;
